/*
 * @Description: 实时定位驾驶舱 — A3「全员最新位置」数据 hook
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchRealtimeLocations } from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import { MOCK_REALTIME_LOCATIONS } from '../mock/locations.mock'
import {
  CockpitEnvelope,
  MemberLocationItem,
  RealtimeLocationsData
} from '../types/cockpit.types'

export interface UseRealtimeLocationsResult {
  /** 全员最新位置列表 */
  members: MemberLocationItem[]
  /** 是否正在请求 */
  loading: boolean
  /** 是否处于 mock 模式（页面据此显示角标提示） */
  isMock: boolean
  /** 手动刷新一次 */
  refresh: () => void
}

/**
 * 拉取实时定位驾驶舱 A3「全员最新位置」。
 * 挂载即请求；仅实时态（当天）按 60s 轮询，历史态（非当天）只取一次、不轮询。
 * mock 模式下直接返回本地 fixtures，不发任何请求。
 *
 * @param groupId - 当前团队长 id（来自 useGroupID）
 * @param isToday - date 是否为当天（决定是否轮询；非当天暂停实时更新）
 * @returns 成员位置列表、加载状态、mock 标记与手动刷新方法
 */
export function useRealtimeLocations(
  groupId: string,
  isToday: boolean,
  pollIntervalMs: number = REALTIME_POLL_INTERVAL_MS
): UseRealtimeLocationsResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [members, setMembers] = useState<MemberLocationItem[]>([])
  const [loading, setLoading] = useState(false)
  // 防止轮询定时器在组件卸载后继续 setState
  const aliveRef = useRef(true)

  const load = useCallback(() => {
    if (isMock) {
      setMembers(MOCK_REALTIME_LOCATIONS.items)
      return
    }
    if (!groupId) return

    setLoading(true)
    dispatch(fetchRealtimeLocations({ groupId }))
      .then((res: CockpitEnvelope<RealtimeLocationsData>) => {
        if (!aliveRef.current) return
        setLoading(false)
        // code===200 即成功；data 为 null 属空态（1002/1004），按空列表处理
        if (res && res.code === 200) {
          setMembers(res.data?.items ?? [])
        }
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
      })
  }, [dispatch, groupId, isMock])

  useEffect(() => {
    aliveRef.current = true
    load()

    // mock 与历史态（非当天）不轮询；仅当天按 60s 轮询刷新最新位置
    if (isMock || !isToday) {
      return () => {
        aliveRef.current = false
      }
    }

    const timer = window.setInterval(load, pollIntervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [load, isMock, isToday, pollIntervalMs])

  return { members, loading, isMock, refresh: load }
}
